import { useState, useEffect, useRef, useCallback } from 'react';
import { Amplify } from 'aws-amplify';
import { signIn, confirmSignIn, signUp, confirmSignUp, signOut, getCurrentUser, fetchAuthSession, resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import awsConfig from './aws-config';
import { track, trackSignupOnce } from './lib/analytics';
import ChatInterface from './components/ChatInterface';
import ProgressView from './components/ProgressView';
import ThreadSelector from './components/ThreadSelector';
import SpaceThreadSelector from './components/SpaceThreadSelector';
import AboutPage from './components/AboutPage';
import TermsPage from './components/TermsPage';
import PrivacyPage from './components/PrivacyPage';
import EssayPage from './components/EssayPage';
import MemoryPage from './components/MemoryPage';
import SettingsPage from './components/SettingsPage';
import PricingTable from './components/PricingTable';
import Landing from './components/Landing';
import Footer from './components/Footer';
import ContextMap from './components/ContextMap';
import SuggestionModal from './components/SuggestionModal';
import DailyDashboard from './components/DailyDashboard';
import { NavRail, BottomTabBar } from './components/Navigation';
import YouSheet from './components/YouSheet';
import MiniPlayer from './components/MiniPlayer';
import Tour from './components/Tour';
import HowToVideo from './components/HowToVideo';
import { useHints } from './lib/hints';
import { getUserState, getAssignments, deleteThread, clearThreadHistory } from './lib/api';
import { Compass, ChevronLeft } from 'lucide-react';

const SUBVIEWS = ['settings', 'about', 'terms', 'privacy'];
const SUBVIEW_TITLES = { settings: 'Settings', about: 'About', terms: 'Terms of Service', privacy: 'Privacy Policy' };

Amplify.configure(awsConfig);

function LoginScreen({ onAuth, onBack, initialSignUp = false, onAbout, onPricing, onTerms, onPrivacy }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(initialSignUp);
  const [confirmCode, setConfirmCode] = useState('');
  const [needsConfirm, setNeedsConfirm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetFlow, setResetFlow] = useState(null); // null | 'request' | 'confirm'
  const [newPassword, setNewPassword] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [authMode, setAuthMode] = useState('code'); // 'code' (email sign-in code) | 'password'
  const [otpFlow, setOtpFlow] = useState(false); // awaiting email sign-in code entry

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (resetFlow === 'request') {
        await resetPassword({ username: email });
        setResetFlow('confirm');
        setResetMessage('Check your email for a verification code.');
      } else if (resetFlow === 'confirm') {
        await confirmResetPassword({ username: email, confirmationCode: confirmCode, newPassword });
        setResetFlow(null);
        setResetMessage('');
        setConfirmCode('');
        setNewPassword('');
        await signIn({ username: email, password: newPassword });
        onAuth();
      } else if (needsConfirm) {
        const { isSignUpComplete } = await confirmSignUp({ username: email, confirmationCode: confirmCode });
        if (isSignUpComplete) {
          trackSignupOnce();
          await signIn({ username: email, password });
          onAuth();
        }
      } else if (isSignUp) {
        const { isSignUpComplete, nextStep } = await signUp({ username: email, password, options: { userAttributes: { email } } });
        if (nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
          setNeedsConfirm(true);
        } else if (isSignUpComplete) {
          trackSignupOnce();
          await signIn({ username: email, password });
          onAuth();
        }
      } else if (otpFlow) {
        const { isSignedIn } = await confirmSignIn({ challengeResponse: confirmCode.trim() });
        if (isSignedIn) onAuth();
      } else if (authMode === 'code') {
        const { isSignedIn, nextStep } = await signIn({
          username: email,
          options: { authFlowType: 'USER_AUTH', preferredChallenge: 'EMAIL_OTP' },
        });
        if (isSignedIn) {
          onAuth();
        } else if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE') {
          setOtpFlow(true);
          setConfirmCode('');
          setResetMessage("We emailed you an 8-digit sign-in code. It's good for 15 minutes.");
        } else {
          setError('Email sign-in is unavailable for this account. Try your password instead.');
        }
      } else {
        await signIn({ username: email, password });
        onAuth();
      }
    } catch (err) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const startResetFlow = () => {
    setResetFlow('request');
    setError('');
    setResetMessage('');
    setConfirmCode('');
    setNewPassword('');
    setNeedsConfirm(false);
    setOtpFlow(false);
    setIsSignUp(false);
  };

  const cancelReset = () => {
    setResetFlow(null);
    setError('');
    setResetMessage('');
    setConfirmCode('');
    setNewPassword('');
  };

  const switchAuthMode = (mode) => {
    setAuthMode(mode);
    setOtpFlow(false);
    setError('');
    setResetMessage('');
    setConfirmCode('');
    setPassword('');
  };

  const cancelOtp = () => {
    setOtpFlow(false);
    setError('');
    setResetMessage('');
    setConfirmCode('');
  };

  // Sign-up always sets a password; sign-in defaults to the email-code path.
  const toggleSignUp = () => {
    setIsSignUp(!isSignUp);
    setError('');
    setResetMessage('');
    setNeedsConfirm(false);
    setOtpFlow(false);
    setConfirmCode('');
    setAuthMode(isSignUp ? 'code' : 'password');
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-950">
      <div className="flex-1 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <Compass size={40} className="text-blue-400 mx-auto mb-3" />
          <h1 className="text-display font-display font-bold bg-brand-gradient bg-clip-text text-transparent">The Navigator</h1>
          <p className="text-sm text-slate-300 mt-2 leading-snug">The mentor who remembers your word — and won't let you off the hook.</p>
          <p className="text-[11px] text-slate-600 mt-2 uppercase tracking-[0.2em]">Clarity · Structure · Accountability</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            required
            disabled={otpFlow}
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-60"
          />
          {(isSignUp || authMode === 'password') && !resetFlow && !otpFlow && (
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              required
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          )}
          {(needsConfirm || resetFlow === 'confirm' || otpFlow) && (
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={confirmCode}
              onChange={(e) => setConfirmCode(e.target.value)}
              placeholder={otpFlow ? 'Sign-in code' : 'Verification code'}
              required
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          )}
          {resetFlow === 'confirm' && (
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New password"
              required
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          )}
          {resetMessage && <p className="text-xs text-green-400">{resetMessage}</p>}
          {error && <p className="text-xs text-red-400">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white rounded-lg py-3 text-sm font-medium transition-colors"
          >
            {loading ? '...' : resetFlow === 'request' ? 'Send Reset Code' : resetFlow === 'confirm' ? 'Reset Password' : needsConfirm ? 'Confirm' : isSignUp ? 'Sign Up' : otpFlow ? 'Sign in' : authMode === 'code' ? 'Email me a sign-in code' : 'Sign In'}
          </button>
        </form>

        {resetFlow ? (
          <button
            onClick={cancelReset}
            className="w-full mt-4 text-xs text-slate-500 hover:text-slate-400 text-center"
          >
            Back to sign in
          </button>
        ) : otpFlow ? (
          <button
            onClick={cancelOtp}
            className="w-full mt-4 text-xs text-slate-500 hover:text-slate-400 text-center"
          >
            Use a different email
          </button>
        ) : (
          <>
            <button
              onClick={toggleSignUp}
              className="w-full mt-4 text-xs text-slate-500 hover:text-slate-400 text-center"
            >
              {isSignUp ? 'Already have an account? Sign in' : 'Need an account? Sign up'}
            </button>
            {!isSignUp && !needsConfirm && (
              authMode === 'code' ? (
                <button
                  onClick={() => switchAuthMode('password')}
                  className="w-full mt-2 text-xs text-slate-500 hover:text-slate-400 text-center"
                >
                  Use a password instead
                </button>
              ) : (
                <>
                  <button
                    onClick={() => switchAuthMode('code')}
                    className="w-full mt-2 text-xs text-slate-500 hover:text-slate-400 text-center"
                  >
                    Email me a code instead
                  </button>
                  <button
                    onClick={startResetFlow}
                    className="w-full mt-2 text-xs text-slate-500 hover:text-slate-400 text-center"
                  >
                    Forgot password?
                  </button>
                </>
              )
            )}
          </>
        )}

        <button
          onClick={onPricing}
          className="w-full mt-2 text-xs text-blue-500 hover:text-blue-400 text-center"
        >
          View Plans
        </button>

        <button
          onClick={onAbout}
          className="w-full mt-2 text-xs text-slate-600 hover:text-slate-400 text-center"
        >
          About The Navigator
        </button>

        <div className="flex justify-center gap-3 mt-3">
          <button
            onClick={onTerms}
            className="text-xs text-slate-600 hover:text-slate-400"
          >
            Terms of Service
          </button>
          <span className="text-xs text-slate-700">|</span>
          <button
            onClick={onPrivacy}
            className="text-xs text-slate-600 hover:text-slate-400"
          >
            Privacy Policy
          </button>
        </div>

        {onBack && (
          <button
            onClick={onBack}
            className="w-full mt-4 text-xs text-slate-600 hover:text-slate-400 text-center"
          >
            &larr; Back
          </button>
        )}
      </div>
      </div>
    </div>
  );
}

function App() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [showAbout, setShowAbout] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  // Shareable essay URL — social posts deep-link to /follow-through-gap
  // (CloudFront rewrites it to the SPA).
  const [showEssay, setShowEssay] = useState(
    () => window.location.pathname === '/follow-through-gap'
  );
  // Trust page — shareable at /how-memory-works (podcast answers, replies).
  const [showMemory, setShowMemory] = useState(
    () => window.location.pathname === '/how-memory-works'
  );
  const [showLogin, setShowLogin] = useState(false);
  const [loginInitialSignUp, setLoginInitialSignUp] = useState(false);
  const [view, setView] = useState('daily'); // 'daily' | 'chat' | 'progress' | 'map' | 'about' | 'settings' | 'terms' | 'privacy'
  const [userState, setUserState] = useState(null);
  const [userData, setUserData] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [activeAssignment, setActiveAssignment] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [patterns, setPatterns] = useState([]);
  const [insights, setInsights] = useState([]);
  const [decisions, setDecisions] = useState(null);
  const [threads, setThreads] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const hints = useHints();
  useEffect(() => { if (userData) hints?.hydrate(userData.hints_seen || {}); }, [userData?.hints_seen]); // eslint-disable-line react-hooks/exhaustive-deps
  const [spaces, setSpaces] = useState([]);
  const [activeSpaceId, setActiveSpaceId] = useState(null);
  const [showSuggestion, setShowSuggestion] = useState(false);
  const [chatSeed, setChatSeed] = useState(null);
  const [showYou, setShowYou] = useState(false);
  const [returnView, setReturnView] = useState('daily');

  useEffect(() => {
    getCurrentUser()
      .then(() => { setAuthed(true); loadUserData(); })
      .catch(() => setAuthed(false))
      .finally(() => setChecking(false));
  }, []);

  // Reset scroll to the top whenever a pre-auth page swaps in. The pages share
  // one scrolling window, so without this a click on a footer link (which sits
  // at the bottom) leaves the next page scrolled past its own header.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [showAbout, showPricing, showTerms, showPrivacy, showMemory, showEssay, showLogin]);

  // Returning from Stripe Checkout (?upgrade=success): the webhook needs a moment
  // to flip the user to trialing/active, so poll a few times to clear the trial
  // gate without a manual refresh, then strip the query param.
  useEffect(() => {
    if (!authed) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('upgrade') !== 'success') return;
    window.history.replaceState({}, '', window.location.pathname);
    let cancelled = false;
    let tries = 0;
    const poll = () => {
      if (cancelled) return;
      tries += 1;
      loadUserData();
      if (tries < 4) setTimeout(poll, 1500);
    };
    poll();
    return () => { cancelled = true; };
  }, [authed]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUserData = async () => {
    try {
      const stateRes = await getUserState();
      setUserState(stateRes.state);
      setUserData(stateRes.user || null);
      setSubscription(stateRes.subscription || null);
      setActiveAssignment(stateRes.active_assignment);
      setSessions(stateRes.recent_sessions || []);
      setPatterns(stateRes.patterns || []);
      setInsights(stateRes.insights || []);
      setDecisions(stateRes.decisions || null);

      // Load threads and set active to default
      const loadedThreads = stateRes.threads || [];
      setThreads(loadedThreads);
      if (loadedThreads.length > 0 && !activeThreadId) {
        const defaultThread = loadedThreads.find(t => t.is_default) || loadedThreads[0];
        setActiveThreadId(defaultThread.id);
      }

      // Load spaces for entrepreneur tier
      const loadedSpaces = stateRes.spaces || [];
      setSpaces(loadedSpaces);
      if (loadedSpaces.length > 0 && !activeSpaceId) {
        const defaultThread = loadedThreads.find(t => t.is_default) || loadedThreads[0];
        const defaultSpaceId = defaultThread?.space_id || loadedSpaces[0]?.id;
        setActiveSpaceId(defaultSpaceId);
      }

      const assignRes = await getAssignments();
      setAssignments(assignRes.assignments || []);
    } catch (err) {
      console.error('Failed to load user data:', err);
    }
  };

  const handleThreadSwitch = useCallback((threadId) => {
    setActiveThreadId(threadId);
    // Update active assignment for the new thread
    const threadAssignment = assignments.find(
      a => a.thread_id === threadId && a.status === 'pending'
    );
    setActiveAssignment(threadAssignment || null);
  }, [assignments]);

  const handleOpenThread = useCallback((threadId) => {
    const thread = threads.find(t => t.id === threadId);
    if (thread?.space_id && thread.space_id !== activeSpaceId) {
      setActiveSpaceId(thread.space_id);
    }
    handleThreadSwitch(threadId);
    setView('chat');
  }, [handleThreadSwitch, threads, activeSpaceId]);

  const handleSpaceSwitch = useCallback((spaceId) => {
    setActiveSpaceId(spaceId);
    const spaceThreads = threads.filter(t => t.space_id === spaceId && t.status === 'active');
    if (spaceThreads.length > 0) {
      handleThreadSwitch(spaceThreads[0].id);
    }
  }, [threads, handleThreadSwitch]);

  const handleViewProgress = useCallback((threadId) => {
    handleThreadSwitch(threadId);
    setView('progress');
  }, [handleThreadSwitch]);

  // Deep-link from a recurring pattern into a coaching session, pre-seeded
  // (but not auto-sent) so the user lands in Chat with the conversation framed.
  const handleConfrontPattern = useCallback((label) => {
    const topic = String(label || 'this pattern').toLowerCase();
    setChatSeed(`I want to talk about a pattern that keeps coming up for me: ${topic}. Help me see what's driving it and what to do differently.`);
    setView('chat');
  }, []);

  // Primary navigation (4 destinations). Coach keeps your current Coach/Progress
  // sub-tab if you're already there; otherwise it opens the conversation.
  const handleNav = useCallback((tab) => {
    if (tab === 'today') setView('daily');
    else if (tab === 'coach') setView(v => (v === 'progress' ? 'progress' : 'chat'));
    else if (tab === 'map') setView('map');
    else if (tab === 'you') setShowYou(true);
  }, []);

  // Account sub-views (Settings/About/Terms/Privacy) open over a remembered
  // origin so the back button returns where the user came from.
  const viewRef = useRef(view);
  useEffect(() => { viewRef.current = view; }, [view]);
  const openSubView = useCallback((target) => {
    if (!SUBVIEWS.includes(viewRef.current)) setReturnView(viewRef.current);
    setView(target);
  }, []);
  const closeSubView = useCallback(() => setView(returnView), [returnView]);

  const handleDeleteThread = useCallback(async (threadId) => {
    try {
      const res = await deleteThread(threadId);
      if (res.deleted) {
        setThreads(prev => prev.filter(t => t.id !== threadId));
        setAssignments(prev => prev.filter(a => a.thread_id !== threadId));
        setSessions(prev => prev.filter(s => s.thread_id !== threadId));
        if (activeThreadId === threadId) {
          setActiveThreadId(prev => {
            const remaining = threads.filter(t => t.id !== threadId);
            const defaultThread = remaining.find(t => t.is_default) || remaining[0];
            return defaultThread ? defaultThread.id : null;
          });
          setActiveAssignment(null);
        }
      }
    } catch (err) {
      console.error('Failed to delete thread:', err);
    }
  }, [activeThreadId, threads]);

  const handleClearThread = useCallback(async (threadId) => {
    try {
      const res = await clearThreadHistory(threadId);
      if (res.cleared) {
        setAssignments(prev => prev.filter(a => a.thread_id !== threadId));
        setSessions(prev => prev.filter(s => s.thread_id !== threadId));
        setPatterns(prev => prev.filter(p => p.thread_id !== threadId));
        // Reset thread counts in local state
        setThreads(prev => prev.map(t =>
          t.id === threadId ? { ...t, session_count: 0, pending_assignments: 0 } : t
        ));
        if (activeAssignment && activeAssignment.thread_id === threadId) {
          setActiveAssignment(null);
        }
      }
    } catch (err) {
      console.error('Failed to clear thread history:', err);
    }
  }, [activeAssignment]);

  const handleAuth = () => {
    setAuthed(true);
    loadUserData();
  };

  const handleSignOut = async () => {
    await signOut();
    setAuthed(false);
    setUserState(null);
    setThreads([]);
    setActiveThreadId(null);
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <Compass size={32} className="text-blue-400 animate-pulse" />
      </div>
    );
  }

  if (!authed) {
    // Single router for the pre-auth pages. Render order below means a stale
    // flag would trap the visitor on an earlier page, so always clear the
    // others before setting the target.
    const goTo = (target) => {
      setShowAbout(target === 'about');
      setShowPricing(target === 'pricing');
      setShowTerms(target === 'terms');
      setShowPrivacy(target === 'privacy');
      setShowMemory(target === 'memory');
      setShowEssay(target === 'essay');
      if (!target) setShowLogin(false);
      window.history.replaceState({}, '', target === 'memory' ? '/how-memory-works' : '/');
    };
    const footerNav = {
      onAbout: () => goTo('about'),
      onPricing: () => goTo('pricing'),
      onMemory: () => goTo('memory'),
      onTerms: () => goTo('terms'),
      onPrivacy: () => goTo('privacy'),
    };

    if (showAbout) {
      return (
        <div className="min-h-screen bg-slate-950 flex flex-col">
          <header className="border-b border-slate-800 px-4 py-3 flex items-center gap-4">
            <button onClick={() => setShowAbout(false)} className="flex items-center gap-2 text-slate-400 hover:text-slate-200 text-sm">
              <Compass size={20} className="text-blue-400" />
              <span className="font-display font-semibold tracking-tight bg-brand-gradient bg-clip-text text-transparent">The Navigator</span>
            </button>
          </header>
          <main className="flex-1 overflow-auto">
            <AboutPage />
            <Footer {...footerNav} />
          </main>
        </div>
      );
    }
    if (showPricing) {
      return (
        <div className="min-h-screen bg-slate-950 flex flex-col">
          <header className="border-b border-slate-800 px-4 py-3 flex items-center gap-4">
            <button onClick={() => setShowPricing(false)} className="flex items-center gap-2 text-slate-400 hover:text-slate-200 text-sm">
              <Compass size={20} className="text-blue-400" />
              <span className="font-display font-semibold tracking-tight bg-brand-gradient bg-clip-text text-transparent">The Navigator</span>
            </button>
          </header>
          <main className="flex-1 overflow-auto">
            <div className="max-w-3xl mx-auto px-4 py-8">
              <div className="text-center mb-8">
                <h2 className="text-xl font-display font-semibold tracking-tight text-slate-200 mb-2">Choose Your Plan</h2>
                <p className="text-sm text-slate-400">The mentor who remembers your word — and won't let you off the hook.</p>
              </div>
              <PricingTable
                currentTier={null}
                onSelectTier={(tier, interval = 'monthly') => {
                  // Pre-auth pick: remember it, start signup; StartTrialGate
                  // highlights the same plan after onboarding.
                  try { localStorage.setItem('nav_pricing_intent', JSON.stringify({ tier, interval, at: Date.now() })); } catch { /* ignore */ }
                  track('pricing_select', { tier, interval });
                  setShowPricing(false);
                  setLoginInitialSignUp(true);
                  setShowLogin(true);
                }}
                isAuthenticated={false}
                checkoutLoading={null}
              />
              <div className="text-center mt-8">
                <button
                  onClick={() => setShowPricing(false)}
                  className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                >
                  &larr; Back to sign in
                </button>
              </div>
            </div>
            <Footer {...footerNav} />
          </main>
        </div>
      );
    }
    if (showTerms) {
      return (
        <div className="min-h-screen bg-slate-950 flex flex-col">
          <header className="border-b border-slate-800 px-4 py-3 flex items-center gap-4">
            <button onClick={() => setShowTerms(false)} className="flex items-center gap-2 text-slate-400 hover:text-slate-200 text-sm">
              <Compass size={20} className="text-blue-400" />
              <span className="font-display font-semibold tracking-tight bg-brand-gradient bg-clip-text text-transparent">The Navigator</span>
            </button>
          </header>
          <main className="flex-1 overflow-auto">
            <TermsPage />
            <Footer {...footerNav} />
          </main>
        </div>
      );
    }
    if (showPrivacy) {
      return (
        <div className="min-h-screen bg-slate-950 flex flex-col">
          <header className="border-b border-slate-800 px-4 py-3 flex items-center gap-4">
            <button onClick={() => setShowPrivacy(false)} className="flex items-center gap-2 text-slate-400 hover:text-slate-200 text-sm">
              <Compass size={20} className="text-blue-400" />
              <span className="font-display font-semibold tracking-tight bg-brand-gradient bg-clip-text text-transparent">The Navigator</span>
            </button>
          </header>
          <main className="flex-1 overflow-auto">
            <PrivacyPage />
            <Footer {...footerNav} />
          </main>
        </div>
      );
    }
    if (showMemory && !showLogin) {
      return (
        <MemoryPage
          onBack={() => { setShowMemory(false); window.history.replaceState({}, '', '/'); }}
          footerNav={footerNav}
        />
      );
    }
    if (showEssay && !showLogin) {
      return (
        <EssayPage
          onGetStarted={() => { setLoginInitialSignUp(true); setShowLogin(true); }}
          onBack={() => { setShowEssay(false); window.history.replaceState({}, '', '/'); }}
          footerNav={footerNav}
        />
      );
    }
    if (showLogin) {
      return (
        <LoginScreen
          onAuth={handleAuth}
          initialSignUp={loginInitialSignUp}
          onBack={() => setShowLogin(false)}
          onAbout={() => setShowAbout(true)}
          onPricing={() => setShowPricing(true)}
          onTerms={() => setShowTerms(true)}
          onPrivacy={() => setShowPrivacy(true)}
        />
      );
    }
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col">
        <main className="flex-1 overflow-auto">
          <Landing
            onGetStarted={() => { setLoginInitialSignUp(true); setShowLogin(true); }}
            onSignIn={() => { setLoginInitialSignUp(false); setShowLogin(true); }}
            {...footerNav}
          />
        </main>
      </div>
    );
  }

  const isCoach = view === 'chat' || view === 'progress';
  const isSubView = SUBVIEWS.includes(view);
  const activeTab = view === 'daily' ? 'today' : isCoach ? 'coach' : view === 'map' ? 'map' : 'you';
  const segBtnClass = (active) =>
    `px-3 py-1 rounded text-xs font-medium transition-colors ${active ? 'bg-slate-700 text-slate-200' : 'text-slate-400 hover:text-slate-200'}`;

  return (
    <div className="h-full bg-slate-950 flex">
      {/* Desktop: left rail */}
      <NavRail activeTab={activeTab} onSelect={handleNav} />

      <div className="flex-1 min-w-0 flex flex-col h-full">
        {/* Context bar — brand on mobile; Coach/Progress segmented control when in Coach.
            Hidden on desktop for non-Coach views (the rail carries the brand). */}
        <header className={`flex-shrink-0 border-b border-slate-800 px-safe-4 pt-safe-3 pb-2 flex items-center gap-3 ${(isCoach || isSubView) ? '' : 'md:hidden'}`}>
          {isSubView ? (
            <button
              onClick={closeSubView}
              className="flex items-center gap-1 -ml-1 min-h-tap text-sm font-medium text-slate-300 hover:text-slate-100 transition-colors"
            >
              <ChevronLeft size={18} /> {SUBVIEW_TITLES[view] || 'Back'}
            </button>
          ) : (
            <>
              <div className="flex items-center gap-2 md:hidden">
                <Compass size={18} className="text-blue-400" />
                <span className="text-sm font-display font-semibold tracking-tight bg-brand-gradient bg-clip-text text-transparent">The Navigator</span>
              </div>
              {isCoach && (
                <div className="flex bg-slate-800/50 rounded-md p-0.5 ml-auto md:ml-0">
                  <button onClick={() => setView('chat')} aria-current={view === 'chat' ? 'page' : undefined} className={segBtnClass(view === 'chat')}>Coach</button>
                  <button onClick={() => setView('progress')} aria-current={view === 'progress' ? 'page' : undefined} className={segBtnClass(view === 'progress')}>Progress</button>
                </div>
              )}
            </>
          )}
        </header>

        {/* Thread selector — only in the Coach destination */}
        {isCoach && threads.length > 0 && (
          subscription?.tier === 'entrepreneur' && spaces.length > 1 ? (
            <SpaceThreadSelector
              spaces={spaces}
              threads={threads}
              activeSpaceId={activeSpaceId}
              activeThreadId={activeThreadId}
              onSelectSpace={handleSpaceSwitch}
              onSelectThread={handleThreadSwitch}
              onThreadsChange={setThreads}
              onDeleteThread={handleDeleteThread}
              onClearThread={handleClearThread}
            />
          ) : (
            <ThreadSelector
              threads={threads}
              activeThreadId={activeThreadId}
              onSelectThread={handleThreadSwitch}
              onThreadsChange={setThreads}
              onDeleteThread={handleDeleteThread}
              onClearThread={handleClearThread}
            />
          )
        )}

        {/* Main content */}
        <main className="flex-1 min-h-0 overflow-hidden relative">
          {view === 'daily' ? (
            <DailyDashboard subscription={subscription} userState={userState} user={userData} sessions={sessions} activeAssignment={activeAssignment} insights={insights} patterns={patterns} assignments={assignments} onDataChange={loadUserData} onNavigate={openSubView} />
          ) : view === 'chat' ? (
            <ChatInterface
              key={activeThreadId}
              threadId={activeThreadId}
              threadName={threads.find(t => t.id === activeThreadId)?.name}
              canCreateRest={subscription?.tier === 'pro' || subscription?.tier === 'entrepreneur'}
              userState={userState}
              activeAssignment={activeAssignment}
              seedText={chatSeed}
              onSeedConsumed={() => setChatSeed(null)}
              voiceEnabled={subscription?.tier === 'pro' || subscription?.tier === 'entrepreneur'}
              lockedOut={!!subscription && subscription.tier !== 'pro' && subscription.tier !== 'entrepreneur'}
              voiceId={userData?.voice_id}
              voiceDefaultOn={userData?.voice_default_on !== false}
              onSessionUpdate={(usage) => console.log('Token usage:', usage)}
              onAssignmentCreated={(assignment) => {
                setActiveAssignment(assignment);
                setAssignments(prev => [assignment, ...prev]);
              }}
              onDataChange={loadUserData}
            />
          ) : view === 'progress' ? (
            <div className="h-full overflow-auto">
              <ProgressView
                assignments={assignments}
                patterns={patterns}
                sessions={sessions}
                threads={threads}
                activeThreadId={activeThreadId}
                subscription={subscription}
                driveLevel={userData?.drive_level || 'mentor'}
                onConfrontPattern={handleConfrontPattern}
                decisions={decisions}
              />
            </div>
          ) : view === 'map' ? (
            <ContextMap subscription={subscription} spaces={spaces} driveLevel={userData?.drive_level || 'mentor'} onOpenThread={handleOpenThread} onViewProgress={handleViewProgress} />
          ) : view === 'settings' ? (
            <SettingsPage
              subscription={subscription}
              user={userData}
            />
          ) : view === 'terms' ? (
            <div className="h-full overflow-auto">
              <TermsPage />
            </div>
          ) : view === 'privacy' ? (
            <div className="h-full overflow-auto">
              <PrivacyPage />
            </div>
          ) : (
            <div className="h-full overflow-auto">
              <AboutPage />
            </div>
          )}
        </main>


        {/* Deep Rest mini-player: keeps playing across tabs */}
        <MiniPlayer />
        <Tour />
        <HowToVideo />

        {/* Mobile: bottom tab bar */}
        <BottomTabBar activeTab={activeTab} onSelect={handleNav} />
      </div>

      <YouSheet
        onTour={() => { setView('daily'); setTimeout(() => hints?.startTour(), 400); }}
        onVideo={() => hints?.openVideo()}
        open={showYou}
        email={userData?.email}
        onClose={() => setShowYou(false)}
        onSettings={() => { openSubView('settings'); setShowYou(false); }}
        onAbout={() => { openSubView('about'); setShowYou(false); }}
        onSuggestion={() => { setShowYou(false); setShowSuggestion(true); }}
        onTerms={() => { openSubView('terms'); setShowYou(false); }}
        onPrivacy={() => { openSubView('privacy'); setShowYou(false); }}
        onSignOut={handleSignOut}
      />
      <SuggestionModal open={showSuggestion} onClose={() => setShowSuggestion(false)} />
    </div>
  );
}

export default App;
